import {
    Box,
    Checkbox,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Paper,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import {
    useSignOutMutation,
    useSignUpMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomTextField } from '../../../layout/styledcomponents/CustomTextField';
import PolicyModal, { Policy } from '../../components/Register/PolicyModal';
import toast from '../../shared/Toast';
import { handleHttpError, validatePhone } from '../../../shared/utils';
import { theme } from '../../../shared/theme';
import { Controller, useForm } from 'react-hook-form';
import { Address } from '../../types/generic';
import AddressAutocomplete from '../../shared/AddressAutocomplete';
import FormTextField from '../../shared/FormTextField';
import FormAutocomplete from '../../shared/FormAutocomplete';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';

type FieldRowProps = {
    id?: string;
    label: string;
    showRequired?: boolean;
    children: React.ReactNode;
    alignTop?: boolean;
};

function FieldRow({
    id,
    label,
    showRequired,
    children,
    alignTop,
}: FieldRowProps) {
    return (
        <Grid
            container
            spacing={1.6}
            alignItems={alignTop ? 'flex-start' : 'center'}
            sx={{ mb: 1.4 }}
        >
            <Grid item xs={12} sm={4}>
                <InputLabel
                    sx={{
                        fontSize: theme.typography.body3.fontSize,
                        mt: alignTop ? 0.8 : 0,
                    }}
                    htmlFor={id}
                    required={!!showRequired}
                >
                    {label}
                </InputLabel>
            </Grid>
            <Grid item xs={12} sm={8}>
                {children}
            </Grid>
        </Grid>
    );
}

export default function RegisterPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [policy, setPolicy] = useState(Policy.PrivacyPolicy);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [signUp] = useSignUpMutation();
    const [signOut] = useSignOutMutation();

    const schema = z
        .object({
            name: z.string().min(2, 'Required'),
            firstName: z.string().min(2, 'Required'),
            lastName: z.string().min(2, 'Required'),
            email: z.string().email('Invalid email'),
            phone: z.string().refine((val) => validatePhone(val), {
                message: 'Invalid phone number',
            }),
            countryCode: z.string().min(1, 'Required'),
            state: z.string().min(2, 'Required'),
            suburb: z.string().min(2, 'Required'),
            streetAddress: z.string().min(2, 'Required'),
            postcode: z.string().min(2, 'Required'),
            manualAddress: z.boolean(),
            password: z
                .string()
                .min(8, 'Must be at least 8 characters')
                .regex(/[A-Z]/, 'Must include at least one uppercase letter')
                .regex(/[a-z]/, 'Must include at least one lowercase letter')
                .regex(/[0-9]/, 'Must include at least one number')
                .regex(
                    /[^A-Za-z0-9]/,
                    'Must include at least one special character',
                ),
            confirmPassword: z.string(),
            privacyPolicy: z.boolean(),
            termsOfService: z.boolean(),
            termsOfPayment: z.boolean(),
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: 'Passwords must match',
            path: ['confirmPassword'],
        });

    type RegisterFormValues = z.infer<typeof schema>;

    const methods = useForm<RegisterFormValues>({
        defaultValues: {
            name: '',
            streetAddress: '',
            suburb: '',
            postcode: '',
            state: '',
            countryCode: '',
            phone: '',
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            manualAddress: false,
            privacyPolicy: false,
            termsOfService: false,
            termsOfPayment: false,
        },
        resolver: zodResolver(schema),
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
    } = methods;

    const manualAddress = watch('manualAddress');
    const name = watch('name');
    const streetAddress = watch('streetAddress');
    const suburb = watch('suburb');
    const postcode = watch('postcode');
    const state = watch('state');
    const countryCode = watch('countryCode');
    const email = watch('email');
    const password = watch('password');
    const confirmPassword = watch('confirmPassword');
    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const phone = watch('phone');
    const privacyPolicy = watch('privacyPolicy');
    const termsOfService = watch('termsOfService');
    const termsOfPayment = watch('termsOfPayment');

    useEffect(() => {
        window.scrollTo(0, 0);
        signOut({});
    }, []);

    useEffect(() => {
        const queryParameters = new URLSearchParams(window.location.search);
        const invite = queryParameters.get('invite');
        if (invite !== '7DAYFRIENDS2026') {
            navigate('/login', {
                replace: true,
            });
        }
    }, []);

    const computedErrors = useMemo(() => {
        const e: string[] = [];
        if (!name) e.push('Company name cannot be empty');
        if (!phone) e.push('Phone name cannot be empty');
        if (!streetAddress) e.push('Address cannot be empty');
        if (!postcode) e.push('Address cannot be empty');
        if (!suburb) e.push('Address cannot be empty');
        if (!state) e.push('Address cannot be empty');
        if (!countryCode) e.push('Address cannot be empty');
        if (!password) e.push('Password cannot be empty');
        if (!confirmPassword) e.push('Password cannot be empty');
        if (!email) e.push('Email cannot be empty');
        if (!privacyPolicy) e.push('Privacy policy should be agreed to');
        if (!termsOfService) e.push('Terms of service should be agreed to');
        if (!termsOfPayment) e.push('Terms of payment should be agreed to');
        return e;
    }, [
        name,
        streetAddress,
        postcode,
        suburb,
        state,
        countryCode,
        password,
        phone,
        confirmPassword,
        email,
        privacyPolicy,
        termsOfService,
        termsOfPayment,
    ]);

    const handlePolicyModal = (p: Policy | ((prevState: Policy) => Policy)) => {
        setPolicy(p);
        setModalOpen(true);
    };

    const onSubmit = async (values: RegisterFormValues) => {
        const requestBody = {
            email: values.email,
            password: values.password,
            confirmPassword: values.confirmPassword,
            phone: values.phone,
            name: values.name,
            streetAddress: values.streetAddress,
            suburb: values.suburb,
            postcode: values.postcode,
            state: values.state,
            countryCode:
                values.countryCode === 'Australia' ||
                values.countryCode === 'AU'
                    ? 'AU'
                    : '',
            firstName: values.firstName,
            lastName: values.lastName,
            privacyPolicy: values.privacyPolicy,
            termsOfService: values.termsOfService,
            termsOfPayment: values.termsOfPayment,
        };

        const res: any = await signUp(requestBody);
        if (res?.error) {
            toast.error(handleHttpError(res?.error));
            return;
        }
        navigate('/thankyou', {
            replace: true,
            state: { firstName: values.firstName },
        });
    };

    const paperStyle = {
        padding: '0px 0px 0px 0px',
        minWidth: 400,
        maxWidth: 500,
        borderRadius: `${theme.palette.custom.button.borderRadius}`,
        boxShadow:
            '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    };

    const allPoliciesChecked =
        termsOfService && privacyPolicy && termsOfPayment;

    const handleAddressChange = (address: Address) => {
        setValue('streetAddress', address.streetAddress, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue('suburb', address.suburb, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue(
            'state',
            address.state as
                | 'NSW'
                | 'QLD'
                | 'TAS'
                | 'VIC'
                | 'ACT'
                | 'WA'
                | 'SA'
                | 'NT',
            {
                shouldDirty: true,
                shouldValidate: true,
            },
        );
        setValue('postcode', address.postcode, {
            shouldDirty: true,
            shouldValidate: true,
        });
        setValue('countryCode', address.country as 'AU', {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const showReq = {
        name: !name || !!errors.name,
        address:
            !streetAddress ||
            !suburb ||
            !state ||
            !postcode ||
            !countryCode ||
            !!errors.streetAddress ||
            !!errors.suburb ||
            !!errors.state ||
            !!errors.postcode ||
            !!errors.countryCode,
        firstName: !firstName || !!errors.firstName,
        lastName: !lastName || !!errors.lastName,
        phone: !phone || !!errors.phone,
        email: !email || !!errors.email,
        password: !password || !!errors.password,
        confirmPassword:
            !confirmPassword ||
            !!errors.confirmPassword ||
            confirmPassword !== password,
    };

    return (
        <Box
            component='main'
            sx={{
                position: 'relative',
                width: '100vw',
                minHeight: 'var(--app-viewport-height)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: theme.palette.custom.loggedOutBackgroundImage,
                backgroundAttachment: { xs: 'scroll', md: 'fixed' },
                backgroundSize: 'cover',
                overflow: 'auto',
                p: { xs: 2, sm: 3 },
            }}
        >
            <Grid component='section' aria-label='SignUp'>
                <Paper elevation={isMobile ? 0 : 10} style={paperStyle}>
                    <Grid
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme.palette.neutral.main,
                            justifyContent: 'center',
                            alignItems: 'center',
                            alignContent: 'center',
                            padding: '35px',
                            borderTopLeftRadius: `${theme.palette.custom.button.borderRadius}`,
                            borderTopRightRadius: `${theme.palette.custom.button.borderRadius}`,
                        }}
                    >
                        <img
                            alt='primarylogo'
                            src={theme.palette.custom.lightLogo}
                            style={{ width: '170px', height: 'auto' }}
                        />
                    </Grid>

                    <Grid sx={{ padding: '0px 40px 40px 40px' }}>
                        <Typography
                            component='h1'
                            variant='h5'
                            color='custom.bodyTextColor'
                            sx={{
                                fontSize: '28px',
                                letterSpacing: '-0.5px',
                                paddingBottom: '20px',
                            }}
                        >
                            Sign Up
                        </Typography>

                        <Typography
                            component='h4'
                            color='textPrimary'
                            fontWeight='bold'
                        >
                            Organisation
                        </Typography>

                        <FieldRow
                            id='org-name'
                            label='Name'
                            showRequired={showReq.name}
                        >
                            <FormTextField
                                name='name'
                                control={control}
                                type='text'
                                placeholder='ABC Pty Ltd'
                            />
                        </FieldRow>
                        <FieldRow
                            label='Address'
                            showRequired={showReq.address}
                            alignTop
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.2,
                                }}
                            >
                                {!manualAddress ? (
                                    <AddressAutocomplete
                                        onChange={handleAddressChange}
                                        height='8px'
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.2,
                                        }}
                                    >
                                        <FormTextField
                                            name='streetAddress'
                                            control={control}
                                            type='text'
                                            placeholder='Street address'
                                        />
                                        <FormTextField
                                            name='suburb'
                                            control={control}
                                            type='text'
                                            placeholder='Suburb'
                                        />
                                        <FormAutocomplete
                                            name='state'
                                            control={control}
                                            options={[
                                                {
                                                    value: 'NSW',
                                                    label: 'NSW',
                                                },
                                                {
                                                    value: 'QLD',
                                                    label: 'QLD',
                                                },
                                                {
                                                    value: 'VIC',
                                                    label: 'VIC',
                                                },
                                                {
                                                    value: 'SA',
                                                    label: 'SA',
                                                },
                                                {
                                                    value: 'WA',
                                                    label: 'WA',
                                                },
                                                {
                                                    value: 'NT',
                                                    label: 'NT',
                                                },
                                                {
                                                    value: 'TAS',
                                                    label: 'TAS',
                                                },
                                                {
                                                    value: 'ACT',
                                                    label: 'ACT',
                                                },
                                            ]}
                                            placeholder='NSW'
                                        />
                                        <FormTextField
                                            name='postcode'
                                            control={control}
                                            type='text'
                                            placeholder='Postcode'
                                        />
                                        <FormAutocomplete
                                            name='countryCode'
                                            control={control}
                                            options={[
                                                {
                                                    value: 'AU',
                                                    label: 'Australia',
                                                },
                                            ]}
                                            placeholder='Australia'
                                        />
                                    </Box>
                                )}

                                <Controller
                                    name='manualAddress'
                                    control={control}
                                    render={({ field }) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Checkbox
                                                checked={!!field.value}
                                                size='small'
                                                onChange={(e, checked) => {
                                                    field.onChange(checked);

                                                    if (checked) {
                                                        setValue(
                                                            'streetAddress',
                                                            '',
                                                            {
                                                                shouldDirty: true,
                                                                shouldValidate: true,
                                                            },
                                                        );
                                                        setValue('suburb', '', {
                                                            shouldDirty: true,
                                                            shouldValidate: true,
                                                        });
                                                        setValue('state', '', {
                                                            shouldDirty: true,
                                                            shouldValidate: true,
                                                        });
                                                        setValue(
                                                            'postcode',
                                                            '',
                                                            {
                                                                shouldDirty: true,
                                                                shouldValidate: true,
                                                            },
                                                        );
                                                        setValue(
                                                            'countryCode',
                                                            '',
                                                            {
                                                                shouldDirty: true,
                                                                shouldValidate: true,
                                                            },
                                                        );
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    opacity: 0.85,
                                                }}
                                            >
                                                Enter address manually
                                            </Typography>
                                        </Box>
                                    )}
                                />
                            </Box>
                        </FieldRow>

                        <Typography
                            component='h4'
                            color='textPrimary'
                            fontWeight='bold'
                        >
                            Account Holder
                        </Typography>

                        <FieldRow
                            id='first-name'
                            label='First name'
                            showRequired={showReq.firstName}
                        >
                            <FormTextField
                                name='firstName'
                                control={control}
                                type='text'
                                placeholder='John'
                            />
                        </FieldRow>

                        <FieldRow
                            id='last-name'
                            label='Last name'
                            showRequired={showReq.lastName}
                        >
                            <FormTextField
                                name='lastName'
                                control={control}
                                type='text'
                                placeholder='Smith'
                            />
                        </FieldRow>

                        <FieldRow
                            id='phone'
                            label='Phone'
                            showRequired={showReq.phone}
                        >
                            <FormTextField
                                name='phone'
                                control={control}
                                placeholder='0414789456'
                                type='tel'
                            />
                        </FieldRow>

                        <FieldRow
                            id='email'
                            label='Email'
                            showRequired={showReq.email}
                        >
                            <FormTextField
                                name='email'
                                control={control}
                                type='text'
                                placeholder='john.smith@abc.com'
                            />
                        </FieldRow>

                        <FieldRow
                            id='password'
                            label='Password'
                            showRequired={showReq.password}
                        >
                            <Controller
                                control={control}
                                name='password'
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        id='password'
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        fullWidth
                                        error={!!errors.password}
                                        helperText={
                                            errors.password?.message as
                                                | string
                                                | undefined
                                        }
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <IconButton
                                                        edge='end'
                                                        tabIndex={-1}
                                                        onMouseDown={(e) =>
                                                            e.preventDefault()
                                                        }
                                                        onClick={() =>
                                                            setShowPassword(
                                                                (v) => !v,
                                                            )
                                                        }
                                                        aria-label={
                                                            showPassword
                                                                ? 'Hide password'
                                                                : 'Show password'
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff
                                                                sx={{
                                                                    fontSize:
                                                                        '16px',
                                                                    opacity: 0.6,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Visibility
                                                                sx={{
                                                                    fontSize:
                                                                        '16px',
                                                                    opacity: 0.6,
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </FieldRow>

                        <FieldRow
                            id='confirm-password'
                            label='Confirm password'
                            showRequired={showReq.confirmPassword}
                        >
                            <Controller
                                control={control}
                                name='confirmPassword'
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        id='confirm-password'
                                        type={
                                            showConfirmPassword
                                                ? 'text'
                                                : 'password'
                                        }
                                        fullWidth
                                        error={!!errors.confirmPassword}
                                        helperText={
                                            errors.confirmPassword?.message as
                                                | string
                                                | undefined
                                        }
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <IconButton
                                                        edge='end'
                                                        tabIndex={-1}
                                                        onMouseDown={(e) =>
                                                            e.preventDefault()
                                                        }
                                                        onClick={() =>
                                                            setShowConfirmPassword(
                                                                (v) => !v,
                                                            )
                                                        }
                                                        aria-label={
                                                            showConfirmPassword
                                                                ? 'Hide confirm password'
                                                                : 'Show confirm password'
                                                        }
                                                    >
                                                        {showConfirmPassword ? (
                                                            <VisibilityOff
                                                                sx={{
                                                                    fontSize:
                                                                        '16px',
                                                                    opacity: 0.6,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Visibility
                                                                sx={{
                                                                    fontSize:
                                                                        '16px',
                                                                    opacity: 0.6,
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </FieldRow>
                    </Grid>

                    <Grid
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            alignItems: 'center',
                            px: '40px',
                        }}
                    >
                        <Grid sx={{ width: '10%' }}>
                            <Checkbox
                                checked={allPoliciesChecked}
                                size='small'
                                sx={{ mt: 0.2, color: 'primary.main' }}
                                onChange={(e, checked) => {
                                    setValue('termsOfService', checked, {
                                        shouldValidate: true,
                                    });
                                    setValue('privacyPolicy', checked, {
                                        shouldValidate: true,
                                    });
                                    setValue('termsOfPayment', checked, {
                                        shouldValidate: true,
                                    });
                                }}
                            />
                        </Grid>

                        <Grid sx={{ width: '90%' }}>
                            <Typography
                                sx={{
                                    color: 'primary.main',
                                    fontSize: 12,
                                    lineHeight: 1.55,
                                    opacity: 0.85,
                                }}
                            >
                                By ticking this checkbox, you agree to our{' '}
                                <Box
                                    component='span'
                                    role='button'
                                    tabIndex={0}
                                    onClick={() =>
                                        handlePolicyModal(Policy.PrivacyPolicy)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ')
                                            handlePolicyModal(
                                                Policy.PrivacyPolicy,
                                            );
                                    }}
                                    sx={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                    }}
                                >
                                    Privacy Policy
                                </Box>
                                ,{' '}
                                <Box
                                    component='span'
                                    role='button'
                                    tabIndex={0}
                                    onClick={() =>
                                        handlePolicyModal(Policy.TermsOfService)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ')
                                            handlePolicyModal(
                                                Policy.TermsOfService,
                                            );
                                    }}
                                    sx={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                    }}
                                >
                                    Terms of Service
                                </Box>{' '}
                                and{' '}
                                <Box
                                    component='span'
                                    role='button'
                                    tabIndex={0}
                                    onClick={() =>
                                        handlePolicyModal(Policy.TermsOfPayment)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ')
                                            handlePolicyModal(
                                                Policy.TermsOfPayment,
                                            );
                                    }}
                                    sx={{
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                    }}
                                >
                                    Terms of Payment
                                </Box>
                                .
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid
                        sx={{
                            marginTop: '30px',
                            pb: '40px',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <CustomButton
                            color='secondary'
                            variant='contained'
                            sx={{
                                height: 44,
                                px: 3.2,
                                borderRadius: 1.6,
                                fontWeight: 900,
                                textTransform: 'none',
                                minWidth: 180,
                            }}
                            disabled={computedErrors.length > 0 || isSubmitting}
                            onClick={handleSubmit(onSubmit)}
                        >
                            Create account
                        </CustomButton>
                    </Grid>
                </Paper>
            </Grid>

            <PolicyModal
                policy={policy}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </Box>
    );
}
